import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type RefObject } from "react";
import { constrainRecordingPosition, recordingPositionKey, type RecordingBox, type RecordingPosition } from "./recording-position";

type Drag = {
  key: string;
  pointerId: number;
  element: HTMLDivElement;
  start: RecordingPosition;
  offset: RecordingPosition;
  origin: RecordingBox;
  width: number;
  height: number;
};
const zero = { x: 0, y: 0 };

export function useRecordingPositions(recording: boolean, playing: boolean, stage: RefObject<HTMLElement | null>) {
  const [positions, setPositions] = useState<Record<string, RecordingPosition>>({});
  const current = useRef(positions);
  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const editable = recording && !playing;

  const update = (key: string, position: RecordingPosition) => {
    current.current = { ...current.current, [key]: position };
    setPositions(current.current);
  };
  const endDrag = () => {
    const active = drag.current;
    drag.current = null;
    if (active?.element.hasPointerCapture(active.pointerId)) active.element.releasePointerCapture(active.pointerId);
    setDragging(null);
  };
  const geometry = (element: HTMLElement, offset: RecordingPosition) => {
    const frame = stage.current;
    if (!frame || !frame.clientWidth || !frame.clientHeight) return null;
    const box = frame.getBoundingClientRect(), actor = element.getBoundingClientRect();
    const width = frame.clientWidth, height = frame.clientHeight;
    return { width, height, origin: {
      x: (actor.left - box.left - frame.clientLeft) / width - offset.x,
      y: (actor.top - box.top - frame.clientTop) / height - offset.y,
      width: actor.width / width, height: actor.height / height,
    } };
  };

  useLayoutEffect(() => {
    const frame = stage.current;
    if (!recording || !frame) return;
    const measure = () => setSize({ width: frame.clientWidth, height: frame.clientHeight });
    const observer = new ResizeObserver(measure);
    observer.observe(frame); measure();
    return () => observer.disconnect();
  }, [recording, stage]);

  useLayoutEffect(() => {
    // Releasing a touch/pen capture also handles rotation or starting playback.
    endDrag();
    if (!recording || playing) return;
    for (const element of stage.current?.querySelectorAll<HTMLDivElement>("[data-recording-character]") ?? []) {
      const key = element.dataset.recordingCharacter!;
      const offset = current.current[key];
      if (!offset) continue;
      const measured = geometry(element, offset);
      if (!measured) continue;
      const bounded = constrainRecordingPosition(offset, measured.origin);
      if (Math.abs(bounded.x - offset.x) > .00001 || Math.abs(bounded.y - offset.y) > .00001) update(key, bounded);
    }
  }, [recording, playing, size.width, size.height]);

  const bind = (key: string, label: string, selected = true) => {
    const movable = editable && selected;
    const offset = positions[key] ?? zero;
    return {
      "data-recording-character": recording ? key : undefined,
      "data-recording-movable": movable ? "true" : undefined,
      "data-recording-dragging": dragging === key ? "true" : undefined,
      role: recording ? "group" : undefined,
      "aria-label": recording ? label : undefined,
      "aria-describedby": movable ? "recording-placement-help" : undefined,
      tabIndex: movable ? 0 : undefined,
      style: recording ? {
        "--recording-offset-x": `${offset.x * size.width}px`,
        "--recording-offset-y": `${offset.y * size.height}px`,
      } as CSSProperties : undefined,
      onPointerDown: movable ? (event: PointerEvent<HTMLDivElement>) => {
        if (!event.isPrimary || event.button !== 0) return;
        const measured = geometry(event.currentTarget, current.current[key] ?? zero);
        if (!measured) return;
        event.preventDefault();
        event.currentTarget.focus({ preventScroll: true });
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { key, pointerId: event.pointerId, element: event.currentTarget,
          start: { x: event.clientX, y: event.clientY }, offset: current.current[key] ?? zero, ...measured };
        setDragging(key);
      } : undefined,
      onPointerMove: movable ? (event: PointerEvent<HTMLDivElement>) => {
        const active = drag.current;
        if (!active || active.pointerId !== event.pointerId || active.key !== key) return;
        update(key, constrainRecordingPosition({
          x: active.offset.x + (event.clientX - active.start.x) / active.width,
          y: active.offset.y + (event.clientY - active.start.y) / active.height,
        }, active.origin));
      } : undefined,
      onPointerUp: movable ? (event: PointerEvent<HTMLDivElement>) => { if (drag.current?.pointerId === event.pointerId) endDrag(); } : undefined,
      onPointerCancel: movable ? endDrag : undefined,
      onLostPointerCapture: movable ? endDrag : undefined,
      onKeyDown: movable ? (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const delta = recordingPositionKey(event.key, event.shiftKey);
        if (event.key === "Home") { event.preventDefault(); update(key, zero); return; }
        if (!delta) return;
        const offset = current.current[key] ?? zero, measured = geometry(event.currentTarget, offset);
        if (!measured) return;
        event.preventDefault();
        update(key, constrainRecordingPosition({ x: offset.x + delta.x, y: offset.y + delta.y }, measured.origin));
      } : undefined,
    };
  };
  return { bind, editable, reset: () => { endDrag(); current.current = {}; setPositions({}); },
    changed: Object.values(positions).some(position => Math.abs(position.x) + Math.abs(position.y) > .00001) };
}
